import { createApp } from 'vue';
import Toast from 'vue-toastification';
import 'vue-toastification/dist/index.css';
import router from '@/plugins/router';
import { store } from '@/plugins/store';
import { ActionType } from '@/plugins/store/actions';
import { i18n } from '@/plugins/i18n';
import ToastOptions from '@/plugins/toastification';
import Authentication from '@/plugins/authentication';
import App from '@/App.vue';
import { hideAppLoader } from '@/lib/appLoader';
import '@/assets/css/main.css';

const STARTUP_TIMEOUT_MS = 8000;

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
    let timer: number;
    const timeout = new Promise<never>((_, reject) => {
        timer = window.setTimeout(() => reject(new Error(`Timed out after ${ms}ms`)), ms);
    });
    return Promise.race([promise, timeout]).finally(() => window.clearTimeout(timer));
}

/** Resolves the user's locale before first render so the UI never swaps language after the loader hides. */
async function loadInitialData() {
    try {
        await withTimeout(
            Promise.all([store.dispatch(ActionType.FetchProfile), store.dispatch(ActionType.FetchLanguages)]),
            STARTUP_TIMEOUT_MS
        );
    } catch (error) {
        // Non-fatal — the UI renders in the fallback locale and AccountLayout retries the fetch on mount
        console.error('Initial data load failed:', error);
    }
}

async function bootstrap() {
    let authFailed = false;
    try {
        await Authentication.initAuth();
    } catch (error) {
        // Mount anyway so the router guard takes over the login redirect
        console.error('Authentication init failed:', error);
        authFailed = true;
    }

    // No token means a login redirect is in flight — keep the loader up until the page navigates away
    if (!authFailed && !Authentication.getToken()) return;

    if (!authFailed) await loadInitialData();

    const app = createApp(App);
    app.use(router).use(store).use(i18n).use(Toast, ToastOptions).mount('#app');

    try {
        await withTimeout(router.isReady(), STARTUP_TIMEOUT_MS);
    } catch (error) {
        // Non-fatal — reveal the app anyway rather than leave the loader spinning
        console.error('Initial navigation did not settle:', error);
    }
    hideAppLoader();
}

bootstrap().catch((error) => {
    console.error('Application bootstrap failed:', error);
    hideAppLoader();
});
